/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('.js-search-product-type-input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      const query = this.getQuery();
      const selectedResult = this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)');

      if (!query.length || selectedResult) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      const searchCandidates = this.getSearchCandidates(searchTerm);
      let firstResultsMarkup = null;
      let mergedResultsMarkup = null;
      let mergedResultCount = 0;
      const isReferenceSearch = this.isReferenceSearchTerm(searchTerm);

      for (const candidate of searchCandidates) {
        const searchParams = this.buildSearchParams(candidate);
        const queryKey = searchParams.replace(' ', '-').toLowerCase();
        let resultsMarkup = this.cachedResults[queryKey];

        if (!resultsMarkup) {
          try {
            resultsMarkup = await this.fetchResultsMarkup(searchParams);
            this.cachedResults[queryKey] = resultsMarkup;
          } catch (error) {
            this.close();
            throw error;
          }
        }

        firstResultsMarkup = firstResultsMarkup || resultsMarkup;

        if (this.hasProductResults(resultsMarkup)) {
          if (isReferenceSearch) {
            mergedResultsMarkup = mergedResultsMarkup
              ? this.mergeProductResults(mergedResultsMarkup, resultsMarkup)
              : resultsMarkup;
            mergedResultCount = this.countProductResults(mergedResultsMarkup);

            if (mergedResultCount >= theme.settings.pSearchLimit) {
              this.renderResults(mergedResultsMarkup);
              return;
            }

            continue;
          }

          this.renderResults(resultsMarkup);
          return;
        }
      }

      if (mergedResultsMarkup) {
        this.renderResults(mergedResultsMarkup);
        return;
      }

      if (firstResultsMarkup) this.renderResults(firstResultsMarkup);
    }

    buildSearchParams(searchTerm) {
      let searchFields = 'title,product_type,variants.title,vendor,body,variants.barcode';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[type]')}=product`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][unavailable_products]')}=last`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      return searchParams;
    }

    isReferenceSearchTerm(searchTerm) {
      const normalized = searchTerm
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const compact = normalized.replace(/[^a-zA-Z0-9]/g, '');
      const wordCount = normalized.split(' ').filter(Boolean).length;
      const hasLetter = /[a-zA-Z]/.test(compact);
      const hasDigit = /\d/.test(compact);

      return /^\d{8,14}$/.test(compact) || (compact.length >= 3 && hasLetter && hasDigit && wordCount <= 3);
    }

    getSearchCandidates(searchTerm) {
      const candidates = [];
      const addCandidate = (candidate) => {
        const value = candidate.replace(/\s+/g, ' ').trim();
        if (value.length >= 2 && !candidates.includes(value)) candidates.push(value);
      };

      const compact = searchTerm.replace(/[\s\-_/.,:;]+/g, '');
      const compactReference = compact.toUpperCase();
      const spaced = searchTerm.replace(/[\-_/.,:;]+/g, ' ');
      const spacedReference = compact
        .replace(/([a-zA-Z]+)(\d+)/g, '$1 $2')
        .replace(/(\d+)([a-zA-Z]+)/g, '$1 $2');
      const hyphenReference = compact.replace(/^([a-zA-Z]{1,6})(\d.*)$/g, '$1-$2');
      const normalizedSearch = spaced
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      const normalizedCompact = compact
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

      if (/^\d{2,5}[A-Z]{1,3}$/.test(compactReference)) {
        this.addConsumableReferenceCandidates(compactReference, addCandidate);
      }

      if (/^\d{3,5}$/.test(compactReference)) {
        this.addNumericConsumableReferenceCandidates(compactReference, addCandidate);
      }

      addCandidate(searchTerm);
      addCandidate(spaced);
      addCandidate(compact);
      addCandidate(spacedReference);
      addCandidate(hyphenReference);
      this.addSpanishAccentCandidates(normalizedSearch, addCandidate);

      if (/^porta/.test(normalizedCompact)
        || /\bordenadores?\s+portatiles?\b/.test(normalizedSearch)
        || /\b(laptops?|notebooks?|portatiles?)\b/.test(normalizedSearch)) {
        addCandidate('portatil');
        addCandidate('port\u00e1til');
        addCandidate('portatiles');
        addCandidate('port\u00e1tiles');
        addCandidate('ordenador portatil');
        addCandidate('ordenador port\u00e1til');
        addCandidate('ordenadores portatiles');
        addCandidate('ordenadores port\u00e1tiles');
        addCandidate('laptop');
        addCandidate('notebook');
      }

      return candidates;
    }

    addSpanishAccentCandidates(normalizedSearch, addCandidate) {
      const accentPairs = [
        ['toner', 't\u00f3ner'],
        ['toners', 't\u00f3ners'],
        ['laser', 'l\u00e1ser'],
        ['impresion', 'impresi\u00f3n'],
        ['multifuncion', 'multifunci\u00f3n'],
        ['inyeccion', 'inyecci\u00f3n'],
        ['termica', 't\u00e9rmica'],
        ['termico', 't\u00e9rmico'],
        ['escaner', 'esc\u00e1ner'],
        ['escaneres', 'esc\u00e1neres'],
        ['informatica', 'inform\u00e1tica'],
        ['papeleria', 'papeler\u00eda'],
        ['portatil', 'port\u00e1til'],
        ['portatiles', 'port\u00e1tiles'],
        ['conexion', 'conexi\u00f3n'],
        ['alimentacion', 'alimentaci\u00f3n'],
        ['proteccion', 'protecci\u00f3n'],
        ['lamina', 'l\u00e1mina'],
        ['laminas', 'l\u00e1minas'],
        ['telefono', 'tel\u00e9fono']
      ];

      const variants = new Set([normalizedSearch]);

      accentPairs.forEach(([plain, accented]) => {
        Array.from(variants).forEach((variant) => {
          const expression = new RegExp(`\\b${plain}\\b`, 'g');
          if (expression.test(variant)) variants.add(variant.replace(expression, accented));
        });
      });

      variants.forEach(addCandidate);
    }

    addConsumableReferenceCandidates(reference, addCandidate) {
      const commonPrefixes = ['CE', 'CF', 'CB', 'CC', 'Q', 'C', 'W', 'CN', 'CH', 'PG', 'CL', 'LC', 'TN', 'DR'];

      commonPrefixes.forEach((prefix) => {
        addCandidate(`${prefix}${reference}`);
        addCandidate(`${prefix}-${reference}`);
      });

      if (/^\d{3,5}[A-Z]{1,3}$/.test(reference)) {
        const trimmedReference = reference.substring(1);
        addCandidate(trimmedReference);
        addCandidate(`HP ${trimmedReference}`);
        addCandidate(`HP ${reference}`);
        addCandidate(`Toner ${trimmedReference}`);
        addCandidate(`Toner ${reference}`);
      }
    }

    addNumericConsumableReferenceCandidates(reference, addCandidate) {
      ['CE', 'CF', 'CB', 'CC', 'Q', 'C', 'W'].forEach((prefix) => {
        addCandidate(`${prefix}${reference}A`);
        addCandidate(`${prefix}-${reference}A`);
      });

      ['TN', 'DR', 'LC', 'PG', 'CL'].forEach((prefix) => {
        addCandidate(`${prefix}${reference}`);
        addCandidate(`${prefix}-${reference}`);
      });
    }

    countProductResults(resultsMarkup) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = resultsMarkup;

      return tmpl.content.querySelectorAll('[id$="-products"] .predictive-search__item:not(.js-submit)').length;
    }

    mergeProductResults(primaryMarkup, secondaryMarkup) {
      const primaryTemplate = document.createElement('template');
      const secondaryTemplate = document.createElement('template');
      primaryTemplate.innerHTML = primaryMarkup;
      secondaryTemplate.innerHTML = secondaryMarkup;

      const primaryList = primaryTemplate.content.querySelector('[id$="-products"] .predictive-search__list');
      const secondaryItems = secondaryTemplate.content.querySelectorAll('[id$="-products"] .predictive-search__item:not(.js-submit)');
      if (!primaryList || !secondaryItems.length) return primaryMarkup;

      const submitItem = primaryList.querySelector('.js-submit');
      const seenUrls = new Set(
        Array.from(primaryList.querySelectorAll('.predictive-search__item:not(.js-submit) .js-search-link'))
          .map((link) => link.getAttribute('href'))
          .filter(Boolean)
      );

      secondaryItems.forEach((item) => {
        const link = item.querySelector('.js-search-link');
        const url = link ? link.getAttribute('href') : '';
        if (!url || seenUrls.has(url) || seenUrls.size >= theme.settings.pSearchLimit) return;

        seenUrls.add(url);
        primaryList.insertBefore(item.cloneNode(true), submitItem);
      });

      return primaryTemplate.innerHTML;
    }

    async fetchResultsMarkup(searchParams) {
      const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
      return resultsEl.innerHTML.replace(/psearch/g, this.input.id);
    }

    hasProductResults(resultsMarkup) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = resultsMarkup;

      return Boolean(tmpl.content.querySelector('[id$="-products"] .predictive-search__item:not(.js-submit)'));
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
